import { useState } from 'react';
import { Copy, Mail, Star, Trash2, Plus, Edit } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/lib/supabase';
import { useEmailComposer } from '@/components/email/EmailComposerProvider';
import { captureError } from '@/lib/sentry';

const EMAIL_LABEL_OPTIONS = ['work', 'personal', 'assistant', 'additional'] as const;

interface ContactEmailsProps {
  contactId: string;
  emails: Array<{
    id: number;
    email: string;
    is_primary: boolean;
    label?: string;
  }>;
  onUpdate: () => void;
}

export function ContactEmails({ contactId, emails, onUpdate }: ContactEmailsProps) {
  const [newEmail, setNewEmail] = useState('');
  const [newLabel, setNewLabel] = useState('work');
  const [isAdding, setIsAdding] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editedEmail, setEditedEmail] = useState('');
  const [editedLabel, setEditedLabel] = useState('work');
  const { toast } = useToast();
  const { openComposer } = useEmailComposer();

  const addEmail = async () => {
    try {
      const { error } = await supabase.from('email_address').insert({
        contact_id: contactId,
        email: newEmail,
        is_primary: emails.length === 0,
        label: newLabel,
      });

      if (error) throw error;

      setNewEmail('');
      setNewLabel('work');
      setIsAdding(false);
      onUpdate();
      
      toast({
        description: "Email address added successfully",
      });
    } catch (error) {
      captureError(error as Error, { feature: 'contacts-contact-emails-add-email' });
      toast({
        title: "Error",
        description: "Failed to add email address",
        variant: "destructive",
      });
    }
  };

  const deleteEmail = async (id: number) => {
    try {
      const { error } = await supabase
        .from('email_address')
        .delete()
        .eq('id', id);

      if (error) throw error;

      onUpdate();
      
      toast({
        description: "Email address removed successfully",
      });
    } catch (error) {
      captureError(error as Error, { feature: 'contacts-contact-emails-delete-email' });
      toast({
        title: "Error",
        description: "Failed to remove email address",
        variant: "destructive",
      });
    }
  };

  const setPrimary = async (id: number) => {
    try {
      // First, set all emails to non-primary
      const { error: resetError } = await supabase
        .from('email_address')
        .update({ is_primary: false })
        .eq('contact_id', contactId);

      if (resetError) throw resetError;

      // Then set the selected email as primary
      const { error: updateError } = await supabase
        .from('email_address')
        .update({ is_primary: true })
        .eq('id', id);

      if (updateError) throw updateError;

      onUpdate();
      
      toast({
        description: "Primary email updated successfully",
      });
    } catch (error) {
      captureError(error as Error, { feature: 'contacts-contact-emails-set-primary' });
      toast({
        title: "Error",
        description: "Failed to update primary email",
        variant: "destructive",
      });
    }
  };

  const updateEmail = async (id: number) => {
    try {
      const { error } = await supabase
        .from('email_address')
        .update({ email: editedEmail, label: editedLabel })
        .eq('id', id);

      if (error) throw error;

      onUpdate();
      setEditingId(null);
      
      toast({
        description: "Email address updated successfully",
      });
    } catch (error) {
      captureError(error as Error, { feature: 'contacts-contact-emails-update-email' });
      toast({
        title: "Error",
        description: "Failed to update email address",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="space-y-2">
      <h2 className="font-semibold text-sm">Email Addresses</h2>

      {emails.map((email) => (
        <div
          key={email.id}
          className="group flex items-center justify-between p-2 rounded-md hover:bg-muted/50 transition-colors"
        >
          <div className="flex items-center gap-2">
            <Mail className="h-3.5 w-3.5 text-muted-foreground" />
            {editingId === email.id ? (
              <div className="flex items-center gap-2">
                <Input
                  value={editedEmail}
                  onChange={(e) => setEditedEmail(e.target.value)}
                  className="h-7 text-xs w-[180px]"
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      updateEmail(email.id);
                    } else if (e.key === 'Escape') {
                      setEditingId(null);
                    }
                  }}
                />
                <Select value={editedLabel} onValueChange={setEditedLabel}>
                  <SelectTrigger className="h-7 text-xs w-[110px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {EMAIL_LABEL_OPTIONS.map((opt) => (
                      <SelectItem key={opt} value={opt} className="text-xs capitalize">{opt}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button size="sm" onClick={() => updateEmail(email.id)} className="h-7 text-xs">Save</Button>
                <Button variant="ghost" size="sm" onClick={() => setEditingId(null)} className="h-7 text-xs">Cancel</Button>
              </div>
            ) : (
              <button
                onClick={() => openComposer({ to: email.email, contactId })}
                className="text-xs hover:underline text-left"
              >
                {email.email}
              </button>
            )}
            {email.is_primary && (
              <Star className="h-3.5 w-3.5 fill-current text-yellow-500" />
            )}
            {email.label && email.label !== 'additional' && (
              <Badge variant="secondary" className="text-xs capitalize">{email.label}</Badge>
            )}
          </div>
          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => {
                setEditingId(email.id);
                setEditedEmail(email.email);
                setEditedLabel(email.label || 'work');
              }}
              className="h-6 w-6"
            >
              <Edit className="h-3.5 w-3.5" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigator.clipboard.writeText(email.email)}
              className="h-6 w-6"
            >
              <Copy className="h-3.5 w-3.5" />
            </Button>
            {!email.is_primary && (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setPrimary(email.id)}
                className="h-6 w-6"
              >
                <Star className="h-3.5 w-3.5" />
              </Button>
            )}
            <Button
              variant="ghost"
              size="icon"
              onClick={() => deleteEmail(email.id)}
              className="h-6 w-6 text-destructive"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
      ))}

      {isAdding ? (
        <div className="flex items-center gap-2 mt-2">
          <Input
            type="email"
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && newEmail.trim()) addEmail();
              if (e.key === 'Escape') setIsAdding(false);
            }}
            placeholder="Enter email address"
            className="h-7 text-xs flex-1"
          />
          <Select value={newLabel} onValueChange={setNewLabel}>
            <SelectTrigger className="h-7 text-xs w-[110px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {EMAIL_LABEL_OPTIONS.map((opt) => (
                <SelectItem key={opt} value={opt} className="text-xs capitalize">{opt}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            size="sm"
            onClick={addEmail}
            disabled={!newEmail.trim()}
            className="h-7 text-xs"
          >
            Add
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsAdding(false)}
            className="h-7 text-xs"
          >
            Cancel
          </Button>
        </div>
      ) : (
        <Button
          variant="ghost"
          className="w-full justify-start text-muted-foreground hover:text-foreground text-xs h-7"
          onClick={() => setIsAdding(true)}
        >
          <Plus className="h-3.5 w-3.5 mr-1.5" />
          Add Email Address
        </Button>
      )}
    </div>
  );
}